import { useEffect, useState } from "react";
import { Image, Settings2, X, Plus, ArrowUp, ArrowDown, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { WIDGETS, DEFAULT_WIDGETS, SPAN } from "@/components/DashboardWidgets";

function AddWidgets({ ids, onAdd }) {
  const missing = Object.keys(WIDGETS).filter((k) => !ids.includes(k));
  return (
    <div className="card-surface border-dashed p-4" data-testid="add-widgets-panel">
      <div className="eyebrow mb-2">Add widgets</div>
      {missing.length === 0 ? <p className="text-[13px] text-slate-500">All widgets are on your dashboard.</p> : (
        <div className="flex flex-wrap gap-2">{missing.map((k) => (
          <button key={k} data-testid={`add-widget-${k}`} onClick={() => onAdd(k)} className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-[12px] font-semibold text-sky-800 transition-colors hover:bg-sky-100">
            <Plus size={12} />{WIDGETS[k].label}
          </button>
        ))}</div>
      )}
    </div>
  );
}

function EditBar({ i, n, onMove, onRemove, label, k }) {
  return (
    <div className="absolute -top-2.5 right-2 z-10 flex items-center gap-0.5 rounded-full border border-slate-200 bg-white px-1 py-0.5 shadow-sm" data-testid={`widget-toolbar-${k}`}>
      <span className="px-1.5 text-[10px] font-semibold text-slate-500">{label}</span>
      <button data-testid={`widget-up-${k}`} disabled={i === 0} onClick={() => onMove(i, -1)} className="rounded-full p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Move earlier"><ArrowUp size={12} /></button>
      <button data-testid={`widget-down-${k}`} disabled={i === n - 1} onClick={() => onMove(i, 1)} className="rounded-full p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Move later"><ArrowDown size={12} /></button>
      <button data-testid={`widget-remove-${k}`} onClick={() => onRemove(k)} className="rounded-full p-1 text-red-500 hover:bg-red-50" title="Remove widget"><X size={12} /></button>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [ids, setIds] = useState(DEFAULT_WIDGETS);
  const [edit, setEdit] = useState(false);
  useEffect(() => { api.get("/dashboard").then((r) => { setD(r.data); if (Array.isArray(r.data.widgets)) setIds(r.data.widgets.filter((k) => WIDGETS[k])); }); }, []);
  const save = async (next) => {
    const prev = ids;
    setIds(next ?? DEFAULT_WIDGETS);
    try { await api.put("/dashboard/widgets", { widgets: next }); } catch (e) { setIds(prev); toast.error(errMsg(e)); }
  };
  const move = (i, dir) => { const n = [...ids]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; save(n); };
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="eyebrow">Overview</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Protocol governance</h1>
        </div>
        <div className="ml-auto flex gap-2">
          {edit && <button data-testid="reset-widgets-btn" onClick={() => { save(null); toast.success("Dashboard reset to default"); }} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"><RotateCcw size={14} />Reset</button>}
          <button data-testid="customise-dashboard-btn" onClick={() => setEdit((e) => !e)} className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-semibold transition-colors ${edit ? "bg-sky-600 text-white hover:bg-sky-700" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
            {edit ? <><Check size={14} />Done</> : <><Settings2 size={14} />Customise</>}
          </button>
        </div>
      </div>
      {edit && <AddWidgets ids={ids} onAdd={(k) => save([...ids, k])} />}
      {ids.length === 0 && !edit && <p className="text-sm text-slate-500" data-testid="no-widgets">No widgets on your dashboard. Choose Customise to add some.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 items-start gap-4" data-testid="dashboard-widgets">
        {ids.map((k, i) => (
          <div key={k} className={`relative ${SPAN[WIDGETS[k].size]} ${edit ? "rounded-lg outline-dashed outline-2 outline-offset-4 outline-sky-300" : ""}`} data-testid={`widget-${k}`}>
            {edit && <EditBar i={i} n={ids.length} k={k} label={WIDGETS[k].label} onMove={move} onRemove={(x) => save(ids.filter((y) => y !== x))} />}
            {WIDGETS[k].render(d)}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[12px] text-slate-500"><Image size={14} /> All seed content is marked SAMPLE – NOT CLINICALLY APPROVED until reviewed by qualified clinicians.</div>
    </div>
  );
}
